/* eslint-disable @typescript-eslint/no-explicit-any */

// 기본 네이티브 이벤트 인터페이스
export interface NativeEvent<T = unknown> {
  type: string
  payload: T
}

// 서재 분석 완료 이벤트 타입
export interface ShelfAnalysisCompletePayload {
  id: string
  status: "COMPLETED" | "FAILED"
  message?: string
  date?: string
}

// 이벤트 타입 매핑 (확장 가능한 구조)
interface EventTypeMap {
  "shelf-photo-analysis-complete": ShelfAnalysisCompletePayload
  // 향후 추가될 이벤트들...
  // "user-profile-update": UserProfilePayload
  // "notification-received": NotificationPayload
}

// 타입 안전한 이벤트 핸들러
export type EventHandler<T> = (payload: T) => void | Promise<void>

// 페이로드 검증 함수들 (확장 가능한 구조)
const validators: Record<keyof EventTypeMap, (payload: unknown) => boolean> = {
  "shelf-photo-analysis-complete": (
    payload: unknown,
  ): payload is ShelfAnalysisCompletePayload => {
    return (
      typeof payload === "object" &&
      payload !== null &&
      typeof (payload as any).id === "string" &&
      ["COMPLETED", "FAILED"].includes((payload as any).status) &&
      typeof (payload as any).date === "string" &&
      (typeof (payload as any).message === "string" ||
        (payload as any).message === undefined)
    )
  },
  // 향후 추가될 검증 함수들...
}

// 간단한 이벤트 브리지
class NativeEventBridge {
  private listeners = new Map<string, Set<EventHandler<any>>>()

  // 이벤트 디스패치
  dispatchEvent(event: NativeEvent): void {
    try {
      // 기본 검증
      if (!event.type || typeof event.type !== "string") {
        console.error(`[NativeEventBridge] 잘못된 이벤트 타입: ${event.type}`)
        return
      }

      // 등록된 이벤트인지 확인 및 페이로드 검증
      const eventType = event.type as keyof EventTypeMap
      const validator = validators[eventType]

      if (!validator) {
        console.warn(
          `[NativeEventBridge] 알 수 없는 이벤트 타입: ${event.type}`,
        )
        return
      }

      if (!validator(event.payload)) {
        console.error(`[NativeEventBridge] 잘못된 페이로드 형식: ${event.type}`)
        return
      }

      const listeners = this.listeners.get(event.type)
      if (!listeners || listeners.size === 0) {
        console.warn(
          `[NativeEventBridge] 등록된 리스너가 없습니다: ${event.type}`,
        )
        return
      }

      // 리스너 실행
      listeners.forEach((callback) => {
        try {
          callback(event.payload)
        } catch (error) {
          console.error(
            `[NativeEventBridge] 리스너 실행 오류 [${event.type}]:`,
            error,
          )
        }
      })
    } catch (error) {
      console.error(`[NativeEventBridge] 이벤트 디스패치 오류:`, error)
    }
  }

  // 이벤트 리스너 등록 (중복 등록 방지)
  addEventListener<T extends keyof EventTypeMap>(
    type: T,
    callback: EventHandler<EventTypeMap[T]>,
  ): boolean {
    if (!this.listeners.has(type)) {
      this.listeners.set(type, new Set())
    }

    const typeListeners = this.listeners.get(type)!

    // 중복 등록 체크
    if (typeListeners.has(callback)) {
      if (process.env.NODE_ENV === "development") {
        console.warn(`[NativeEventBridge] 이미 등록된 리스너입니다: ${type}`)
      }
      return false // 이미 등록되어 있음
    }

    typeListeners.add(callback)
    if (process.env.NODE_ENV === "development") {
      console.log(
        `[NativeEventBridge] 리스너 등록: ${type} (총 ${typeListeners.size}개)`,
      )
    }
    return true // 새로 등록됨
  }

  // 이벤트 리스너 해제
  removeEventListener<T extends keyof EventTypeMap>(
    type: T,
    callback: EventHandler<EventTypeMap[T]>,
  ): boolean {
    const listeners = this.listeners.get(type)
    if (!listeners) {
      return false // 등록된 타입이 없음
    }

    const removed = listeners.delete(callback)
    if (removed) {
      if (process.env.NODE_ENV === "development") {
        console.log(
          `[NativeEventBridge] 리스너 해제: ${type} (남은 개수: ${listeners.size})`,
        )
      }

      // 리스너가 모두 제거되면 Map에서도 제거
      if (listeners.size === 0) {
        this.listeners.delete(type)
      }
    }

    return removed // 해제 성공/실패 여부 반환
  }
}

// 전역 인스턴스
export const nativeEventBridge = new NativeEventBridge()

// window 객체 타입 확장
declare global {
  interface Window {
    readlogReverseBridge?: (event: NativeEvent) => void
  }
}

// 브라우저에서 네이티브에서 호출할 수 있도록 전역 함수 등록
if (typeof window !== "undefined") {
  window.readlogReverseBridge = (event: NativeEvent) => {
    nativeEventBridge.dispatchEvent(event)
  }
}

// 타입 export
export type { EventTypeMap }
